import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES, BRAND } from "@/lib/site";

const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || "/send-quote.php";

const initial = {
  name: "",
  phone: "",
  email: "",
  suburb: "",
  service: "",
  message: "",
  company: "", // honeypot — must stay empty
};

export default function QuoteForm({ compact = false, idPrefix = "qf" }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.suburb || !form.service) {
      toast.error("Please fill name, phone, suburb and service.");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 6) {
      toast.error("Please enter a valid phone number — we need it to call you back.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        suburb: form.suburb,
        service: form.service,
        message: form.message,
        company: form.company, // honeypot
      };
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Submission failed");
      }
      setDone(true);
      setForm(initial);
      toast.success("Quote request received — we'll call you shortly.");
    } catch (err) {
      toast.error(
        err?.message || "Could not send. Please call us instead."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-900"
        data-testid="quote-form-success"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-blue-600" />
        <h3 className="mt-4 font-display text-2xl font-bold">Thanks — we've got it.</h3>
        <p className="mt-2 text-sm text-slate-600">
          A Flowgrid plumber will call you back shortly on the number you provided. For emergencies, please call{" "}
          <span className="font-bold text-blue-700">{BRAND.phone}</span>.
        </p>
        <Button
          variant="outline"
          className="mt-6"
          onClick={() => setDone(false)}
          data-testid="quote-form-reset-btn"
        >
          Send another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`grid text-slate-900 ${compact ? "gap-3" : "gap-4"}`}
      data-testid="quote-form"
      id="quote"
    >
      {/* Honeypot — hidden from humans, catches bots. Must stay empty. */}
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={set("company")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
      />
      <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-4`}>
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-name`}>Full name *</Label>
          <Input
            id={`${idPrefix}-name`}
            value={form.name}
            onChange={set("name")}
            placeholder="Jane Citizen"
            data-testid="quote-form-name"
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-phone`}>Phone number *</Label>
          <Input
            id={`${idPrefix}-phone`}
            value={form.phone}
            onChange={set("phone")}
            placeholder="04xx xxx xxx"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            data-testid="quote-form-phone"
            required
          />
        </div>
      </div>

      <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-4`}>
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-email`}>
            Email <span className="text-xs text-slate-500">(optional)</span>
          </Label>
          <Input
            id={`${idPrefix}-email`}
            value={form.email}
            onChange={set("email")}
            type="email"
            placeholder="you@email.com"
            data-testid="quote-form-email"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`${idPrefix}-suburb`}>Suburb *</Label>
          <Input
            id={`${idPrefix}-suburb`}
            value={form.suburb}
            onChange={set("suburb")}
            placeholder="e.g. Newtown, Marrickville, Sydney"
            data-testid="quote-form-suburb"
            required
          />
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label>Service required *</Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger data-testid="quote-form-service">
            <SelectValue placeholder="What do you need help with?" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.slug} value={s.title}>
                {s.title}
              </SelectItem>
            ))}
            <SelectItem value="Not sure / Other">Not sure / Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-message`}>Describe the job</Label>
        <Textarea
          id={`${idPrefix}-message`}
          value={form.message}
          onChange={set("message")}
          placeholder="e.g. Hot water unit cold since this morning, Rheem 250L."
          rows={compact ? 3 : 4}
          data-testid="quote-form-message"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="quote-form-submit"
        className="mt-2 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-6 text-base font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {submitting ? "Sending..." : "Request My Free Quote"}
      </Button>

      <p className="text-xs text-slate-500">
        We'll call you back fast. Emergency? Dial{" "}
        <span className="font-bold text-blue-700">{BRAND.phone}</span>.
      </p>
    </form>
  );
}
